import React, { useEffect } from 'react';
import { Link } from 'react-router-dom';
import { ArrowLeft, Download, Shield } from 'lucide-react';
import { Button } from '../components/ui/button';
import { useTranslation } from '../i18n';

const privacyContent = {
  fr: {
    title: 'Politique de confidentialité & cookies – Motivya (UE / RGPD)',
    lastUpdate: 'Dernière mise à jour : 19 février 2026',
    intro: 'Cette page décrit comment vos données personnelles sont traitées lorsque vous utilisez le site et/ou l\'application Motivya (la "Plateforme"). Elle sera mise à jour au fur et à mesure de l\'évolution du service.',
    sections: [
      {
        title: '1) Responsable du traitement',
        content: `Prodinvideo SPRL (droit belge)
N° BCE : BE0885.194.185
Adresse : Avenue Ferdauci 28, 1020 Bruxelles, Belgique
Contact RGPD : contact@motivya.be
DPO : non désigné à ce jour.`
      },
      {
        title: '2) Données collectées (strict nécessaire)',
        content: `**Utilisateurs (participants)**
• Identité et contact : nom, prénom, email, téléphone.
• Compte : identifiants, paramètres, langue (mot de passe stocké de manière sécurisée).
• Activité : recherches, réservations, annulations/no-show, échanges avec le support.

**Coachs**
• Profil public : bio, disciplines, zones, disponibilités, tarifs.
• Gestion d'activité : séances publiées, participants, historique.
• Données de paiement/facturation : informations nécessaires au versement et à la conformité comptable (sans stocker les données de carte).

**Données techniques**
• Logs, adresse IP, identifiants techniques, données de navigation et cookies (voir section 8).

**Important** : Motivya n'a pas vocation à collecter des données de santé. Évitez d'en saisir dans des champs libres.`
      },
      {
        title: '3) Finalités (pourquoi nous traitons vos données)',
        content: `• Créer et gérer votre compte.
• Permettre la mise en relation, la réservation et l'exécution des séances.
• Gérer paiements, facturation, prévention de la fraude et incidents.
• Support client et gestion des litiges.
• Statistiques d'usage et amélioration du service.
• Communications : emails transactionnels ; marketing uniquement selon les règles applicables (opt-in/cookies).`
      },
      {
        title: '4) Bases légales',
        content: `Selon le cas :
• Contrat (compte, réservation, exécution du service).
• Obligation légale (comptabilité, fiscalité, sécurité).
• Intérêt légitime (sécurité, lutte anti-fraude, amélioration du service).
• Consentement (cookies non essentiels, marketing lorsque requis).`
      },
      {
        title: '5) Partage des données',
        content: `Vos données peuvent être partagées avec :
• Les équipes Prodinvideo habilitées (support/ops/finance).
• Des sous-traitants (hébergement, emailing transactionnel, prestataire de paiement, analytics, support), encadrés contractuellement.
• Dans la logique marketplace : un coach reçoit les informations nécessaires pour exécuter la séance (ex. prénom, statut de réservation, informations pratiques). Le participant voit les informations publiques du coach (profil/séance/avis).`
      },
      {
        title: '6) Conservation',
        content: `Nous conservons vos données uniquement le temps nécessaire :
• Compte utilisateur/coach : pendant l'activité du compte puis suppression/anonymisation après une période d'inactivité raisonnable.
• Transactions/factures : durée conforme aux obligations légales belges.
• Logs sécurité : durée limitée et proportionnée.

Vous pouvez demander la suppression dans les limites des obligations légales.`
      },
      {
        title: '7) Vos droits',
        content: `Vous disposez des droits : accès, rectification, effacement, limitation, opposition, portabilité, retrait du consentement.

Pour exercer vos droits : contactez contact@motivya.be en précisant l'email du compte et l'objet de la demande.

Vous pouvez aussi introduire une réclamation auprès de l'autorité de protection des données compétente (Belgique).`
      },
      {
        title: '8) Cookies',
        content: `Motivya utilise :
• Cookies nécessaires : sécurité, authentification, fonctionnement du service.
• Cookies de mesure d'audience / fonctionnels / marketing : uniquement selon configuration et, si requis, avec consentement via un bandeau cookies.

Un lien "Gestion des cookies" sera accessible pour modifier vos choix.`
      },
      {
        title: '9) Transferts hors EEE',
        content: 'Si certains prestataires traitent des données hors EEE, Prodinvideo met en place des garanties appropriées (ex. clauses contractuelles types), lorsque nécessaire.'
      },
      {
        title: '10) Sécurité',
        content: 'Mesures proportionnées : chiffrement en transit (TLS), contrôle d\'accès, journalisation, procédures internes de gestion d\'incident.'
      },
      {
        title: '11) Mise à jour de cette page',
        content: 'Cette politique peut être modifiée. La date en en-tête indique la dernière mise à jour. En cas de changement majeur, une information pourra être publiée sur la Plateforme.'
      }
    ]
  },
  en: {
    title: 'Privacy & Cookie Policy – Motivya (EU / GDPR)',
    lastUpdate: 'Last updated: 19 February 2026',
    intro: 'This page describes how your personal data is processed when you use the Motivya website and/or application (the "Platform"). It will be updated as the service evolves.',
    sections: [
      {
        title: '1) Data Controller',
        content: `Prodinvideo SPRL (Belgian law)
BCE No.: BE0885.194.185
Address: Avenue Ferdauci 28, 1020 Brussels, Belgium
GDPR Contact: contact@motivya.be
DPO: not designated at this time.`
      },
      {
        title: '2) Data Collected (strictly necessary)',
        content: `**Users (participants)**
• Identity and contact: surname, first name, email, phone.
• Account: credentials, settings, language (password stored securely).
• Activity: searches, bookings, cancellations/no-shows, support exchanges.

**Coaches**
• Public profile: bio, disciplines, areas, availability, rates.
• Activity management: published sessions, participants, history.
• Payment/billing data: information necessary for payment and accounting compliance (without storing card data).

**Technical data**
• Logs, IP address, technical identifiers, browsing data and cookies (see section 8).

**Important**: Motivya does not intend to collect health data. Please avoid entering it in free text fields.`
      },
      {
        title: '3) Purposes (why we process your data)',
        content: `• Create and manage your account.
• Enable matchmaking, booking and execution of sessions.
• Manage payments, billing, fraud prevention and incidents.
• Customer support and dispute management.
• Usage statistics and service improvement.
• Communications: transactional emails; marketing only in accordance with applicable rules (opt-in/cookies).`
      },
      {
        title: '4) Legal Bases',
        content: `As applicable:
• Contract (account, booking, service execution).
• Legal obligation (accounting, taxation, security).
• Legitimate interest (security, anti-fraud, service improvement).
• Consent (non-essential cookies, marketing when required).`
      },
      {
        title: '5) Data Sharing',
        content: `Your data may be shared with:
• Authorised Prodinvideo teams (support/ops/finance).
• Subcontractors (hosting, transactional email, payment provider, analytics, support), contractually bound.
• In the marketplace logic: a coach receives the information necessary to carry out the session (e.g. first name, booking status, practical information). The participant sees the coach's public information (profile/session/reviews).`
      },
      {
        title: '6) Retention',
        content: `We retain your data only for the time necessary:
• User/coach account: during account activity, then deletion/anonymisation after a reasonable period of inactivity.
• Transactions/invoices: duration in accordance with Belgian legal obligations.
• Security logs: limited and proportionate duration.

You may request deletion within the limits of legal obligations.`
      },
      {
        title: '7) Your Rights',
        content: `You have the following rights: access, rectification, erasure, restriction, objection, portability, withdrawal of consent.

To exercise your rights: contact contact@motivya.be specifying the account email and the subject of your request.

You may also lodge a complaint with the competent data protection authority (Belgium).`
      },
      {
        title: '8) Cookies',
        content: `Motivya uses:
• Necessary cookies: security, authentication, service operation.
• Audience measurement / functional / marketing cookies: only according to configuration and, if required, with consent via a cookie banner.

A "Cookie Settings" link will be available to modify your choices.`
      },
      {
        title: '9) Transfers outside the EEA',
        content: 'If certain providers process data outside the EEA, Prodinvideo implements appropriate safeguards (e.g. standard contractual clauses), where necessary.'
      },
      {
        title: '10) Security',
        content: 'Proportionate measures: encryption in transit (TLS), access control, logging, internal incident management procedures.'
      },
      {
        title: '11) Updates to this page',
        content: 'This policy may be amended. The date at the top indicates the last update. In the event of a major change, information may be published on the Platform.'
      }
    ]
  },
  nl: {
    title: 'Privacy- en cookiebeleid – Motivya (EU / AVG)',
    lastUpdate: 'Laatst bijgewerkt: 19 februari 2026',
    intro: 'Deze pagina beschrijft hoe uw persoonsgegevens worden verwerkt wanneer u de website en/of applicatie Motivya (het "Platform") gebruikt. Ze wordt bijgewerkt naarmate de dienst evolueert.',
    sections: [
      {
        title: '1) Verwerkingsverantwoordelijke',
        content: `Prodinvideo BVBA (Belgisch recht)
KBO-nr.: BE0885.194.185
Adres: Avenue Ferdauci 28, 1020 Brussel, België
AVG-contact: contact@motivya.be
DPO: momenteel niet aangesteld.`
      },
      {
        title: '2) Verzamelde gegevens (strikt noodzakelijk)',
        content: `**Gebruikers (deelnemers)**
• Identiteit en contact: naam, voornaam, e-mail, telefoon.
• Account: inloggegevens, instellingen, taal (wachtwoord veilig opgeslagen).
• Activiteit: zoekopdrachten, boekingen, annuleringen/no-shows, uitwisselingen met de support.

**Coaches**
• Openbaar profiel: bio, disciplines, zones, beschikbaarheid, tarieven.
• Activiteitenbeheer: gepubliceerde sessies, deelnemers, geschiedenis.
• Betalings-/facturatiegegevens: informatie nodig voor betaling en boekhoudkundige conformiteit (zonder kaartgegevens op te slaan).

**Technische gegevens**
• Logs, IP-adres, technische identificatoren, browsegegevens en cookies (zie sectie 8).

**Belangrijk**: Motivya is niet bedoeld om gezondheidsgegevens te verzamelen. Vermijd deze in te voeren in vrije tekstvelden.`
      },
      {
        title: '3) Doeleinden (waarom we uw gegevens verwerken)',
        content: `• Uw account aanmaken en beheren.
• Het in contact brengen, boeken en uitvoeren van sessies mogelijk maken.
• Betalingen, facturatie, fraudepreventie en incidenten beheren.
• Klantenondersteuning en geschillenbeheer.
• Gebruiksstatistieken en verbetering van de dienst.
• Communicatie: transactionele e-mails; marketing alleen volgens de geldende regels (opt-in/cookies).`
      },
      {
        title: '4) Rechtsgronden',
        content: `Naargelang het geval:
• Contract (account, boeking, uitvoering van de dienst).
• Wettelijke verplichting (boekhouding, fiscaliteit, veiligheid).
• Gerechtvaardigd belang (veiligheid, fraudebestrijding, verbetering van de dienst).
• Toestemming (niet-essentiële cookies, marketing indien vereist).`
      },
      {
        title: '5) Delen van gegevens',
        content: `Uw gegevens kunnen worden gedeeld met:
• Geautoriseerde Prodinvideo-teams (support/ops/financiën).
• Onderaannemers (hosting, transactionele e-mail, betalingsprovider, analytics, support), contractueel gebonden.
• In de marktplaatslogica: een coach ontvangt de informatie die nodig is om de sessie uit te voeren (bv. voornaam, boekingsstatus, praktische informatie). De deelnemer ziet de openbare informatie van de coach (profiel/sessie/beoordelingen).`
      },
      {
        title: '6) Bewaring',
        content: `We bewaren uw gegevens alleen zo lang als nodig:
• Gebruikers-/coachaccount: tijdens de activiteit van het account, daarna verwijdering/anonimisering na een redelijke periode van inactiviteit.
• Transacties/facturen: duur conform de Belgische wettelijke verplichtingen.
• Beveiligingslogs: beperkte en evenredige duur.

U kunt verwijdering aanvragen binnen de grenzen van de wettelijke verplichtingen.`
      },
      {
        title: '7) Uw rechten',
        content: `U beschikt over de volgende rechten: inzage, rectificatie, wissing, beperking, bezwaar, overdraagbaarheid, intrekking van toestemming.

Om uw rechten uit te oefenen: neem contact op via contact@motivya.be met vermelding van het e-mailadres van het account en het onderwerp van uw verzoek.

U kunt ook een klacht indienen bij de bevoegde gegevensbeschermingsautoriteit (België).`
      },
      {
        title: '8) Cookies',
        content: `Motivya gebruikt:
• Noodzakelijke cookies: beveiliging, authenticatie, werking van de dienst.
• Cookies voor publieksmeting / functioneel / marketing: alleen volgens configuratie en, indien vereist, met toestemming via een cookiebanner.

Een link "Cookiebeheer" zal beschikbaar zijn om uw keuzes te wijzigen.`
      },
      {
        title: '9) Doorgifte buiten de EER',
        content: 'Indien bepaalde dienstverleners gegevens buiten de EER verwerken, treft Prodinvideo passende waarborgen (bv. standaardcontractbepalingen), waar nodig.'
      },
      {
        title: '10) Beveiliging',
        content: 'Evenredige maatregelen: encryptie tijdens overdracht (TLS), toegangscontrole, logging, interne procedures voor incidentbeheer.'
      },
      {
        title: '11) Updates van deze pagina',
        content: 'Dit beleid kan worden gewijzigd. De datum bovenaan geeft de laatste update aan. Bij een belangrijke wijziging kan informatie op het Platform worden gepubliceerd.'
      }
    ]
  }
};

function renderContent(text) {
  return text.split('\n').map((line, i) => {
    if (line.startsWith('**') && line.includes('**')) {
      const parts = line.split('**');
      return (
        <p key={i} className="mt-4 mb-1">
          <strong className="text-white">{parts[1]}</strong>{parts[2]}
        </p>
      );
    }
    if (line.startsWith('•')) {
      return <p key={i} className="pl-4 py-0.5 text-white/70">{line}</p>;
    }
    if (line.trim() === '') {
      return <div key={i} className="h-2" />;
    }
    return <p key={i} className="text-white/70">{line}</p>;
  });
}

export default function PrivacyPage() {
  const { lang } = useTranslation();
  const content = privacyContent[lang] || privacyContent.fr;

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  const handleDownloadPDF = () => {
    const printWindow = window.open('', '_blank');
    const langLabel = { fr: 'Français', en: 'English', nl: 'Nederlands' }[lang] || 'Français';
    
    const sectionsHtml = content.sections.map(s => `
      <h2 style="font-size:16px;font-weight:700;margin:24px 0 8px;color:#1a1a1a;">${s.title}</h2>
      <div style="font-size:13px;line-height:1.7;color:#333;white-space:pre-line;">${s.content.replace(/\*\*/g, '')}</div>
    `).join('');

    printWindow.document.write(`<!DOCTYPE html><html><head>
      <title>${content.title}</title>
      <style>
        body { font-family: 'Helvetica Neue', Arial, sans-serif; max-width: 700px; margin: 40px auto; padding: 0 24px; color: #333; }
        h1 { font-size: 20px; font-weight: 700; color: #111; margin-bottom: 4px; }
        .meta { font-size: 12px; color: #888; margin-bottom: 24px; }
        .lang { font-size: 11px; background: #f0f0f0; padding: 2px 8px; border-radius: 4px; }
        @media print { body { margin: 20px; } }
      </style>
    </head><body>
      <h1>${content.title}</h1>
      <p class="meta">${content.lastUpdate} <span class="lang">${langLabel}</span></p>
      <p style="font-size:13px;line-height:1.7;color:#555;">${content.intro}</p>
      ${sectionsHtml}
      <hr style="margin:32px 0;border:none;border-top:1px solid #eee;">
      <p style="font-size:11px;color:#aaa;text-align:center;">Motivya – Prodinvideo SPRL – contact@motivya.be</p>
    </body></html>`);
    printWindow.document.close();
    setTimeout(() => printWindow.print(), 500);
  };

  return (
    <div className="min-h-screen bg-[var(--bg-primary)]">
      {/* Header */}
      <div className="sticky top-0 z-50 bg-[var(--bg-primary)]/95 backdrop-blur-sm border-b border-white/10">
        <div className="max-w-4xl mx-auto px-4 py-4 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-2 text-white/60 hover:text-white transition-colors">
            <ArrowLeft className="w-5 h-5" />
            <span className="text-sm">Motivya</span>
          </Link>
          <Button
            onClick={handleDownloadPDF}
            variant="outline"
            size="sm"
            className="flex items-center gap-2"
            data-testid="download-privacy-pdf"
          >
            <Download className="w-4 h-4" />
            PDF
          </Button>
        </div>
      </div>

      {/* Content */}
      <div className="max-w-4xl mx-auto px-4 py-10">
        <div className="flex items-center gap-3 mb-2">
          <Shield className="w-8 h-8 text-[var(--brand-cyan)]" />
          <h1 className="text-2xl sm:text-3xl font-bold text-white">{content.title}</h1>
        </div>
        <p className="text-sm text-white/40 mb-8">{content.lastUpdate}</p>
        
        <p className="text-white/70 leading-relaxed mb-8">{content.intro}</p>

        <div className="space-y-8">
          {content.sections.map((section, idx) => (
            <section key={idx} className="border-l-2 border-white/10 pl-5">
              <h2 className="text-lg font-semibold text-white mb-3">{section.title}</h2>
              <div className="text-sm leading-relaxed">
                {renderContent(section.content)}
              </div>
            </section>
          ))}
        </div>

        {/* Footer */}
        <div className="mt-12 pt-8 border-t border-white/10 text-center">
          <p className="text-xs text-white/30">Motivya – Prodinvideo SPRL – contact@motivya.be</p>
        </div>
      </div>
    </div>
  );
}
